import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config';

dayjs.extend(relativeTime);

const SOURCES = ['TH', 'GA', 'TR', 'GPS', 'PI', 'SDLY', 'DWH_CUSTOM', 'WEEKLY', 'SCAN_VISIT'];

const formatDate = (value) => {
  if (!value)
    return '—';

  const date = dayjs(value);
  return `${date.format('L LT')} (${date.fromNow()})`;
};

const splitHours = (hours) => {
  if (hours != null && hours % 24 === 0)
    return { value: hours / 24, unit: 'days' };

  return { value: hours ?? 24, unit: 'hours' };
};

const toHours = (value, unit) => (unit === 'days' ? value * 24 : value);

const emptyForm = { id: null, source: SOURCES[0], client: '', thresholdValue: 24, thresholdUnit: 'hours', emails: '' };

const inputClass = 'w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm';

export default function IndexationWatches({ fetchWithAuth }) {
  const { t } = useTranslation();
  const [watches, setWatches] = useState([]);
  const [sourceClients, setSourceClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');

  const fetchWatches = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings/freshness-watches`);
      const data = await response.json();
      setWatches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching freshness watches:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  const fetchSourceClients = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/settings/freshness-watches/source-clients`);
      const data = await response.json();
      setSourceClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching source/client pairs:', err);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchWatches();
    fetchSourceClients();
  }, [fetchWatches, fetchSourceClients]);

  const clientsForSource = useMemo(
    () => sourceClients.filter((sc) => sc.source === form?.source).map((sc) => sc.client),
    [sourceClients, form]
  );

  const openAdd = () => {
    setError('');
    setForm({ ...emptyForm });
  };

  const openEdit = (watch) => {
    setError('');
    const { value, unit } = splitHours(watch.threshold_hours);
    setForm({
      id: watch.id,
      source: watch.source,
      client: watch.client,
      thresholdValue: value,
      thresholdUnit: unit,
      emails: (watch.emails || []).join(', '),
    });
  };

  const closeForm = () => {
    setForm(null);
    setError('');
  };

  const handleSubmit = async () => {
    setError('');

    const payload = {
      source: form.source,
      client: form.client,
      threshold_hours: toHours(Number(form.thresholdValue), form.thresholdUnit),
      emails: form.emails.split(',').map((e) => e.trim()).filter(Boolean),
    };

    if (!payload.client)
      return setError(t('Client is required'));

    if (!Number.isFinite(payload.threshold_hours) || payload.threshold_hours <= 0)
      return setError(t('A positive threshold is required'));

    const isEdit = form.id != null;
    const url = isEdit
      ? `${API_BASE_URL}/api/settings/freshness-watches/${form.id}`
      : `${API_BASE_URL}/api/settings/freshness-watches`;

    try {
      const response = await fetchWithAuth(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok)
        return setError(data.error || t('An error occurred'));

      closeForm();
      fetchWatches();
    } catch (err) {
      console.error('Error saving watch:', err);
      setError(t('An error occurred'));
    }
  };

  const handleDelete = async (watch) => {
    if (!window.confirm(t('Delete watch for {{source}} / {{client}}?', { source: watch.source, client: watch.client })))
      return;

    try {
      await fetchWithAuth(`${API_BASE_URL}/api/settings/freshness-watches/${watch.id}`, { method: 'DELETE' });
      fetchWatches();
    } catch (err) {
      console.error('Error deleting watch:', err);
    }
  };

  const isEdit = form?.id != null;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={openAdd}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm"
        >
          {t('Add a watch')}
        </button>
      </div>

      {form && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">
            {isEdit ? t('Edit watch') : t('Add a watch')}
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1 text-gray-600 dark:text-gray-300">{t('Source')}</label>
              <select
                className={inputClass}
                value={form.source}
                disabled={isEdit}
                onChange={(e) => setForm((f) => ({ ...f, source: e.target.value, client: '' }))}
              >
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm mb-1 text-gray-600 dark:text-gray-300">{t('Client')}</label>
              <input
                className={inputClass}
                list="freshness-watch-clients"
                value={form.client}
                disabled={isEdit}
                placeholder={t('Select or type a client')}
                onChange={(e) => setForm((f) => ({ ...f, client: e.target.value.toUpperCase() }))}
              />
              <datalist id="freshness-watch-clients">
                {clientsForSource.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>

            <div>
              <label className="block text-sm mb-1 text-gray-600 dark:text-gray-300">{t('Freshness threshold')}</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  className={inputClass}
                  value={form.thresholdValue}
                  onChange={(e) => setForm((f) => ({ ...f, thresholdValue: e.target.value }))}
                />
                <select
                  className={inputClass}
                  value={form.thresholdUnit}
                  onChange={(e) => setForm((f) => ({ ...f, thresholdUnit: e.target.value }))}
                >
                  <option value="hours">{t('hours')}</option>
                  <option value="days">{t('days')}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm mb-1 text-gray-600 dark:text-gray-300">{t('Warning emails (comma-separated)')}</label>
              <input
                className={inputClass}
                value={form.emails}
                placeholder="ops@globe-groupe.com, data@globe-groupe.com"
                onChange={(e) => setForm((f) => ({ ...f, emails: e.target.value }))}
              />
            </div>
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex gap-2 justify-end">
            <button onClick={closeForm} className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600 text-sm">
              {t('Cancel')}
            </button>
            <button onClick={handleSubmit} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm">
              {t('Save')}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">{t('Source')}</th>
                <th className="px-4 py-3 text-left">{t('Client')}</th>
                <th className="px-4 py-3 text-left">{t('Freshness threshold')}</th>
                <th className="px-4 py-3 text-left">{t('Warning emails')}</th>
                <th className="px-4 py-3 text-left">{t('Last picture taken')}</th>
                <th className="px-4 py-3 text-left">{t('Status')}</th>
                <th className="px-4 py-3 text-right">{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">{t('Loading...')}</td></tr>
                )
                : watches.length === 0
                  ? (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">{t('No watched source/client yet')}</td></tr>
                  )
                  : watches.map((watch) => {
                    const { value, unit } = splitHours(watch.threshold_hours);
                    return (
                      <tr key={watch.id} className={`border-b border-gray-200 dark:border-gray-700 last:border-0 ${watch.stale ? 'bg-red-50 dark:bg-red-900/30' : ''}`}>
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{watch.source}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{watch.client}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{value} {t(unit)}</td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{(watch.emails || []).join(', ') || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatDate(watch.last_picture_at)}</td>
                        <td className="px-4 py-3">
                          {watch.stale
                            ? <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100">{t('stale')}</span>
                            : <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">{t('ok')}</span>}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button onClick={() => openEdit(watch)} className="text-blue-600 dark:text-blue-400 mr-4">{t('Edit')}</button>
                          <button onClick={() => handleDelete(watch)} className="text-red-600 dark:text-red-400">{t('Delete')}</button>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
